import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { LEAD_RANKING_CLASS, LEAD_RANKING_LABEL } from "@/lib/format";
import { Plus, Search, LayoutGrid, Table2 } from "lucide-react";
import NewClientModal from "@/components/NewClientModal";

export default function Clients() {
    const [clients, setClients] = useState([]);
    const [categories, setCategories] = useState([]);
    const [collaborators, setCollaborators] = useState([]);
    const [q, setQ] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [rankFilter, setRankFilter] = useState("");
    const [view, setView] = useState("table");
    const [showNew, setShowNew] = useState(false);

    const load = async () => {
        const params = {};
        if (q) params.q = q;
        if (catFilter) params.category_id = catFilter;
        if (rankFilter) params.lead_ranking = rankFilter;
        const { data } = await api.get("/clients", { params });
        setClients(data);
    };

    useEffect(() => {
        (async () => {
            const [cats, colls] = await Promise.all([
                api.get("/settings/categories"),
                api.get("/collaborators"),
            ]);
            setCategories(cats.data);
            setCollaborators(colls.data);
        })();
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, catFilter, rankFilter]);

    const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
    const collById = Object.fromEntries(collaborators.map((c) => [c.id, c]));

    return (
        <div className="p-6" data-testid="clients-page">
            <div className="flex items-baseline justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Clienti</h1>
                    <div className="text-sm text-neutral-500">{clients.length} risultati</div>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="new-client-btn"
                    className="inline-flex items-center gap-1.5 bg-neutral-900 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-neutral-800">
                    <Plus size={14} /> Nuovo cliente
                </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-3 flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[180px]">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca per nome, azienda, email…"
                        className="w-full pl-7 pr-3 py-1.5 text-sm border border-neutral-200 rounded" />
                </div>
                <select data-testid="filter-category" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutte le categorie</option>
                    {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
                <select data-testid="filter-ranking" value={rankFilter} onChange={(e) => setRankFilter(e.target.value)} className="text-sm border border-neutral-200 rounded px-2 py-1.5">
                    <option value="">Tutti i lead</option>
                    <option value="caldo">Caldo</option>
                    <option value="tiepido">Tiepido</option>
                    <option value="freddo">Freddo</option>
                </select>
                <div className="border border-neutral-200 rounded flex overflow-hidden">
                    <button onClick={() => setView("table")} className={`px-2 py-1.5 text-xs ${view === "table" ? "bg-neutral-900 text-white" : "text-neutral-600"}`} data-testid="view-table"><Table2 size={13} /></button>
                    <button onClick={() => setView("grid")} className={`px-2 py-1.5 text-xs ${view === "grid" ? "bg-neutral-900 text-white" : "text-neutral-600"}`} data-testid="view-grid"><LayoutGrid size={13} /></button>
                </div>
            </div>

            {clients.length === 0 && (
                <div className="bg-white border border-neutral-200 rounded-md p-10 text-center" data-testid="empty-state">
                    <div className="font-display text-neutral-800 font-medium">Nessun cliente</div>
                    <div className="text-sm text-neutral-500 mt-1">Crea il primo cliente per iniziare.</div>
                </div>
            )}

            {view === "table" && clients.length > 0 && (
                <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="clients-table">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                            <tr>
                                <th className="text-left px-4 py-2 font-semibold">Nome</th>
                                <th className="text-left px-4 py-2 font-semibold">Azienda</th>
                                <th className="text-left px-4 py-2 font-semibold">Categoria</th>
                                <th className="text-left px-4 py-2 font-semibold">Lead</th>
                                <th className="text-left px-4 py-2 font-semibold">Procurato da</th>
                                <th className="text-left px-4 py-2 font-semibold">Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map((c) => (
                                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`client-row-${c.id}`}>
                                    <td className="px-4 py-2">
                                        <Link to={`/clienti/${c.id}`} className="font-medium text-neutral-900 hover:underline">{c.first_name} {c.last_name}</Link>
                                        {c.role && <div className="text-xs text-neutral-500">{c.role}</div>}
                                    </td>
                                    <td className="px-4 py-2 text-neutral-700">{c.company || "—"}</td>
                                    <td className="px-4 py-2">
                                        {catById[c.category_id] ? (
                                            <span className="inline-flex items-center gap-1 text-xs">
                                                <span className="w-2 h-2 rounded-full" style={{ background: catById[c.category_id].color }} />
                                                {catById[c.category_id].name}
                                            </span>
                                        ) : <span className="text-neutral-400 text-xs">—</span>}
                                    </td>
                                    <td className="px-4 py-2">
                                        <span className={`inline-flex text-xs px-1.5 py-0.5 rounded border ${LEAD_RANKING_CLASS[c.lead_ranking] || ""}`}>{LEAD_RANKING_LABEL[c.lead_ranking] || "—"}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-neutral-700">{collById[c.collaborator_id]?.name || "—"}</td>
                                    <td className="px-4 py-2 text-neutral-600 text-xs">{c.email || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {view === "grid" && clients.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {clients.map((c) => (
                        <Link key={c.id} to={`/clienti/${c.id}`} data-testid={`client-card-${c.id}`}
                            className="bg-white border border-neutral-200 rounded-md p-4 hover:border-neutral-300 hover:shadow-sm transition">
                            <div className="flex items-start justify-between">
                                <div className="min-w-0">
                                    <div className="font-medium text-neutral-900 truncate">{c.first_name} {c.last_name}</div>
                                    <div className="text-xs text-neutral-500 truncate">{c.company || "—"}</div>
                                </div>
                                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${LEAD_RANKING_CLASS[c.lead_ranking] || ""}`}>{LEAD_RANKING_LABEL[c.lead_ranking]}</span>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                                {catById[c.category_id] && (
                                    <span className="inline-flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full" style={{ background: catById[c.category_id].color }} />
                                        {catById[c.category_id].name}
                                    </span>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {showNew && (
                <NewClientModal
                    categories={categories}
                    collaborators={collaborators}
                    onClose={() => setShowNew(false)}
                    onCreated={() => { setShowNew(false); load(); }}
                />
            )}
        </div>
    );
}
